/**
 * Mağaza read model (GET /shop, v0.1.13): the listing table with lifetime and 30 day views,
 * favourites, 90 day orders and (v0.1.233) the Etsy stats page read, the last orders with their
 * items, the country split, twelve months of revenue and the Marketplace Insights terms. Amounts are cents; the panel converts to its micro-USD unit
 * when it formats. Read only: the Etsy reader writes, the panel shows.
 */

import type { Instant } from './types.js';

export const LISTING_STATES = ['active', 'inactive', 'draft', 'expired', 'sold_out', 'removed'] as const;
export type ListingState = (typeof LISTING_STATES)[number];

export const LISTING_STATE_LABELS: Readonly<Record<ListingState, string>> = Object.freeze({
  active: 'Aktif',
  inactive: 'Pasif',
  draft: 'Taslak',
  expired: 'Süresi doldu',
  sold_out: 'Tükendi',
  removed: 'Kaldırıldı',
});

export const ORDER_STATUSES = ['open', 'paid', 'completed', 'refunded', 'partially_refunded', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Readonly<Record<OrderStatus, string>> = Object.freeze({
  open: 'Açık',
  paid: 'Ödendi',
  completed: 'Tamamlandı',
  refunded: 'İade',
  partially_refunded: 'Kısmi iade',
  cancelled: 'İptal',
});

export interface ShopListing {
  listingId: string;
  title: string;
  state: ListingState | string;
  priceCents: number | null;
  url: string | null;
  tags: string[];
  /** Lifetime views from Etsy's API (null before the first read). */
  viewsTotal: number | null;
  /** Views gained over the last 30 days (null until two daily snapshots exist). */
  views30: number | null;
  /** Current favourites. */
  favorites: number | null;
  orders90: number;
  revenue90Cents: number;
  /** Etsy stats page, last 30 days (null before the first browser read). */
  visits30: number | null;
  visitsReadOn: Instant | null;
  traffic: { label: string; visits: number }[];
  terms: { term: string; visits: number }[];
  /** Monthly visits of the last twelve months that were read, oldest first ("2026-10"). */
  months: { month: string; visits: number; sold: number | null; revenueCents: number | null }[];
  lastSyncedAt: Instant;
}

export interface ShopMarketTerm {
  term: string;
  readOn: Instant;
  /** Etsy wide searches over the last 30 days. */
  searches: number;
  changePct: number | null;
  results: number | null;
  conversion: string | null;
  similar: { term: string; searches: number; results: number | null; conversion: string | null }[];
}

export interface ShopOrderItem {
  title: string;
  quantity: number;
  priceCents: number;
  listingId: string | null;
}

export interface ShopOrder {
  orderId: string;
  orderedAt: Instant;
  country: string | null;
  status: OrderStatus | string;
  totalCents: number;
  discountCents: number;
  couponCode: string | null;
  channel: string | null;
  items: ShopOrderItem[];
}

export interface ShopCountry {
  code: string;
  orders: number;
  monthOrders: number;
  revenueCents: number;
}

export interface ShopMonth {
  year: number;
  month: number;
  label: string;
  cents: number;
  orders: number;
}

export interface ShopView {
  asOf: Instant;
  lastSyncedAt: Instant | null;
  listingCounts: { active: number; total: number };
  listings: ShopListing[];
  orders: ShopOrder[];
  countries: ShopCountry[];
  monthly: ShopMonth[];
  marketTerms: ShopMarketTerm[];
}

export function listingStateLabel(state: string): string {
  return (LISTING_STATE_LABELS as Record<string, string>)[state] ?? state;
}

export function orderStatusLabel(status: string): string {
  return (ORDER_STATUS_LABELS as Record<string, string>)[status] ?? status;
}

/** Cents to the panel's micro-USD unit (formatUsd). */
export function centsToMicro(cents: number): number {
  return cents * 10_000;
}
